import React, { useEffect } from 'react';
import { BrowserRouter, Route, Routes, useParams } from 'react-router-dom';
import { ToastProvider } from '@toqio/toqio-web-components';
import { BridgeProvider } from './bridge/BridgeProvider';
import { useBridge } from './bridge/useBridge';
import { ThemeProvider } from './theme/ThemeProvider';
import { GlobalStyle } from './theme/GlobalStyle';
import { useAutoFlowReadyOnPaint } from './bridge/useFlowReady';
import { I18nProvider } from './i18n/I18nProvider';
import { setApiHeaders, setAuthToken } from './api/client';
import { flows } from './flows';

/**
 * Blocks rendering until the host has sent `init`. Shows a minimal skeleton
 * while waiting. This is what makes the embedding feel native: the user
 * never sees an unthemed flash of content.
 */
const InitGate: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { initialized, theme, locale, token, headers } = useBridge();

  // Keep the axios client's token in sync with the bridge.
  useEffect(() => {
    setAuthToken(token);
  }, [token]);

  // Keep the axios client's deployment headers in sync with the bridge.
  useEffect(() => {
    setApiHeaders(headers);
  }, [headers]);

  if (!initialized) {
    // Transparent placeholder. The host overlays its own spinner on top of
    // the iframe until we emit `flow:ready`, so rendering visible loading
    // chrome here would only flash a second loading state through to the
    // user on the cold start. See bridge/protocol.ts → FlowReadyMessage.
    return <div style={{ minHeight: '100vh' }} aria-hidden />;
  }

  return (
    <ThemeProvider theme={theme}>
      <GlobalStyle />
      <I18nProvider locale={locale}>
        <ToastProvider>{children}</ToastProvider>
      </I18nProvider>
    </ThemeProvider>
  );
};

export const FlowLoader: React.FC = () => {
  const { flowId } = useParams<{ flowId: string }>();
  const flow = flowId ? flows[flowId] : undefined;

  // For `auto` flows (the default), drop the host overlay automatically once
  // the flow has painted. `manual` flows own this via useFlowReady(). An
  // unknown flow stays `auto` so its error screen reveals (no stuck spinner).
  useAutoFlowReadyOnPaint(flow?.readiness !== 'manual');

  if (!flow) {
    return (
      <div style={{ padding: 24, fontFamily: 'system-ui, sans-serif' }}>
        <h1>Unknown flow</h1>
        <p>
          No flow registered under id <code>{flowId}</code>.
        </p>
      </div>
    );
  }

  const FlowComponent = flow.component;
  return <FlowComponent />;
};

export const App: React.FC = () => (
  <BridgeProvider>
    <InitGate>
      <BrowserRouter basename="/flows">
        <Routes>
          <Route path=":flowId/*" element={<FlowLoader />} />
          <Route
            path="*"
            element={
              <div style={{ padding: 24 }}>
                Open a flow via <code>/flows/&lt;flowId&gt;</code>.
              </div>
            }
          />
        </Routes>
      </BrowserRouter>
    </InitGate>
  </BridgeProvider>
);
